import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import {
  ApprovalMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import {
  ApprovalFlowProcessSettingSchema,
  type ApproverCandidate,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { SaveApprovalFlowRequestSchema } from '@ebalance/proto/bffuser/v1/approval_flow_service_pb'
import { useTranslation } from 'react-i18next'

import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  chakra,
  toaster,
} from '@enechain/ecloud-designsystem'
import { FaArrowRightRegular } from '@enechain/ecloud-icons'

import { StageEditModal } from '~/components/approval/StageEditModal'
import {
  type StageLocal,
  commonInitialStages,
  makeEmptyStage,
} from '~/components/approval/stage'
import { DisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { Badge } from '~/components/common/Badge'
import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import { HintTooltip } from '~/components/common/HintTooltip'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import { ApprovalMatrix } from '~/pages/AdminApprovalFlow/components/ApprovalMatrix'
import { ApprovalModeGuide } from '~/pages/AdminApprovalFlow/components/ApprovalModeGuide'
import { AutoBidPanel } from '~/pages/AdminApprovalFlow/components/AutoBidPanel'
import { SectionViewModal } from '~/pages/AdminApprovalFlow/components/SectionViewModal'
import { WarningTriangleIcon } from '~/pages/AdminApprovalFlow/components/icons'
import { SAVE_ERROR_ATTR } from '~/pages/AdminApprovalFlow/components/matrixStyles'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import {
  type CellKey,
  type ProcessDef,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import { useAutoBidSection } from '~/pages/AdminApprovalFlow/hooks/useAutoBidSection'
import { useResetApprovalFlow } from '~/pages/AdminApprovalFlow/hooks/useResetApprovalFlow'
import { useSaveApprovalFlow } from '~/pages/AdminApprovalFlow/hooks/useSaveApprovalFlow'
import '~/pages/AdminApprovalFlow/models/autoBid'
import {
  type AutoBidSlotsByArea,
  NO_AUTO_BID_SLOTS,
  isAutoBidRuleUnsaved,
} from '~/pages/AdminApprovalFlow/models/autoBidSlot'
import { convertProcessSetting } from '~/pages/AdminApprovalFlow/models/converter'
import {
  type MatrixAction,
  type ProcessState,
  aggregateMode,
  canSave,
  getAllCellKeys,
  hasMissingApprover,
  isCellIncomplete,
} from '~/pages/AdminApprovalFlow/models/reducer'
import { saveProcess } from '~/pages/AdminApprovalFlow/models/saveProcess'
import { approvalStageRange } from '~/pages/AdminApprovalFlow/models/stageRange'

type Props = {
  processDef: ProcessDef
  processState: ProcessState | undefined
  candidates: ApproverCandidate[]
  defaultOpen?: boolean
  dispatch: (action: MatrixAction) => void
  /**
   * 自動入札ルールの側ごとの状態 (自動入札を持つ業務処理だけ非空)。 離脱 Alert の
   * 判定が参照で比べるので、 呼び出し側は内容が同じ間は同じ参照を渡す。
   */
  autoBidSlots?: AutoBidSlotsByArea
  /**
   * 「保存して移動」で未設定のため保存できなかった回数 (AC-APF-011d)。 増えるたびに
   * 区画を開き、 「保存」 を押した時と同じ Alert・フォーカスで知らせる。
   */
  leaveBlockedCount?: number
  /**
   * 増えるたびに最初の未設定へフォーカスを送る。 未設定の業務処理が複数ある時は
   * 最初の 1 件にだけ渡す (design: focusFirstSaveErr(blocked[0]))。
   */
  leaveFocusCount?: number
}

/**
 * 「リセット」 (= 既定へ初期化) ボタンを出すか。
 *
 * **[spec 差異] 出さない (2026-08-24 デザイナー判断)。**
 * 運用設定グループの他画面 (需要想定の自動補正 / 計画自動提出設定 / FIT・FIP設定) は
 * 元から「変更を破棄」 +「保存」 の 2 つだけで、 承認フローもこれに揃える。
 * design 8080 (`admin/approval-flow.html` の `showResetBtn = false`) も同じ形で、
 * プロト側のコメントが「ユーザー指示による先行対応であり PdM 確認に回す前提」と
 * 明記している。
 *
 * ⚠ spec 正本 `docs/specs/admin/operation/approval/approval-flow.md` の
 * **AC-APF-012a は [P0] でリセットボタンと destructive 確認モーダルを要求**しており、
 * 論点表でも「B: design から削除」 は明示的に不採用 (A で PdM 確定)。
 * よってこれは spec に対する意図的な先行差異で、 **spec 更新前に「spec 違反のバグ」
 * として戻さないこと**。 PdM 確認は issue #6178。
 *
 * 復帰はこの定数を `true` にするだけ (ハンドラ `handleReset` / 確認モーダル /
 * i18n `actions.reset` はすべて残してある)。
 *
 * なお、 リセットは空 payload を送って backend に既定値を materialize させる
 * 経路で、 **`auto_bid_rule` を DELETE できる唯一の UI 導線**でもある
 * (`go/internal/backend/application/usecase/approvalflow/approval_flow.go` の
 * `isResetRequest`)。 非表示の間はこの操作が画面から行えない。
 */
const SHOW_RESET_BUTTON = false

/** 保存を止めているセルが無い時の空集合 (毎回の再生成による再描画を避ける)。 */
const NO_CELL_KEYS: ReadonlySet<CellKey> = new Set<CellKey>()

/** 自動入札ルールを持たない業務処理の既定値 (拒否時点の記録と参照で比べるため固定する)。 */

/**
 * 印の付いた要素から、 フォーカスを当てる操作を選ぶ。 セルを直す操作はボタン側
 * (「承認者未設定」 / 「ルール編集」) にあるのでボタンを優先し、 ボタンの無いセル
 * (モード未設定) だけセレクトに当てる (8080 `admin/approval-flow.html` の
 * focusFirstSaveErr と同じ順)。
 */
function fixControlOf(marked: HTMLElement): HTMLElement {
  if (marked instanceof HTMLButtonElement) return marked
  return (
    marked.querySelector<HTMLElement>('button') ??
    marked.querySelector<HTMLElement>('input:not(:disabled)') ??
    marked
  )
}

export const ProcessAccordion = ({
  processDef,
  processState,
  candidates,
  defaultOpen = false,
  dispatch,
  autoBidSlots = NO_AUTO_BID_SLOTS,
  leaveBlockedCount = 0,
  leaveFocusCount = 0,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(defaultOpen)
  // ユーザーが「個別設定」を明示選択したか (AC-004)。 セルが一様 (= aggregate が
  // 非 UNSPECIFIED) でもユーザーが個別設定を選べばマトリクスを展開する。 これが無いと
  // 「個別設定」選択が no-op になり、 全セル同一モード (新規テナント既定 = 全 AUTO 等)
  // でマトリクスに到達できなくなる (デグレ修正)。
  const [individualView, setIndividualView] = useState(false)
  // 一括「全て要承認」時の「承認者一括編集」モーダルの開閉
  const [bulkStageModalOpen, setBulkStageModalOpen] = useState(false)
  // 設定状況モーダル (個別設定の業務処理名から AC-APF-057、 一括 = 要承認 の要約から AC-APF-074)
  const [sectionViewOpen, setSectionViewOpen] = useState(false)
  // destructive リセット確認ダイアログの開閉 (AC-012a)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  // 変更を破棄確認ダイアログの開閉 (AC-APF-011c)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const { mutateAsync: saveAsync, isPending: isSaving } = useSaveApprovalFlow()
  const { mutateAsync: resetAsync, isPending: isResetting } =
    useResetApprovalFlow()
  const isPending = isSaving || isResetting

  const hasState = processState !== undefined
  const allKeys: CellKey[] = useMemo(
    () =>
      hasState
        ? getAllCellKeys(processDef.businessProcess, processDef.areas)
        : [],
    [hasState, processDef],
  )

  const isAutoBidProcess =
    processDef.businessProcess === BusinessProcess.SPOT_BID ||
    processDef.businessProcess === BusinessProcess.INTRADAY_BID

  /**
   * 自動で自動入札ルールを一度も保存していないセル (AC-APF-070 の (b))。
   * 件数 (バッジ・Alert) と、 「保存」を押した後のセルのエラー表示 (AC-APF-071) は
   * この集合と下の `approverIncompleteKeys` の 2 つだけから出す (数とエラーのセルを
   * ずらさないため)。
   */
  const unsavedAutoBidKeys = useMemo((): ReadonlySet<CellKey> => {
    if (processState === undefined || !isAutoBidProcess) return NO_CELL_KEYS
    const keys = new Set<CellKey>()
    for (const area of processDef.areas) {
      const key = makeNoSectionCellKey(processDef.businessProcess, area)
      const cell = processState.cells.get(key)
      if (
        cell?.mode === ApprovalMode.AUTO &&
        isAutoBidRuleUnsaved(autoBidSlots, area)
      ) {
        keys.add(key)
      }
    }
    return keys
  }, [processState, processDef, autoBidSlots, isAutoBidProcess])
  const unsavedAutoBidCount = unsavedAutoBidKeys.size

  /** 承認者が未設定のセル (AC-APF-070 の (a)。 自動入札ルール未保存分を含まない)。 */
  const approverIncompleteKeys = useMemo(
    (): readonly CellKey[] =>
      processState === undefined
        ? []
        : allKeys.filter((key) =>
            isCellIncomplete(processState.cells.get(key)),
          ),
    [processState, allKeys],
  )
  const approverIncompleteCount = approverIncompleteKeys.length

  /** 保存をブロックする総数 (バッジの件数表示に使う)。 */
  const incompleteCount = approverIncompleteCount + unsavedAutoBidCount

  /**
   * 欠落 (登録済承認者が候補不在 = eauth 上で削除・無効化, AC-APF-026d) を
   * 業務処理単位で 1 つでも含むか (Design Note D3)。
   *
   * 一括「全て要承認」時 (showMatrix=false) は per-cell surface
   * (MatrixModeCell) が無いため、 未設定は要設定バッジで可視なのに欠落だけ
   * 不可視になる非対称を解消する非ブロック指標。 全セル同一なら件数が
   * 意味を持たない (件数爆発) ため boolean のみで示し、 `incompleteCount` /
   * `canSaveProcess` には **混ぜない** (D2: 欠落は警告のみで保存・完了を
   * ブロックしない)。
   */
  const candidateIds = useMemo(
    () => new Set(candidates.map((c) => c.memberId)),
    [candidates],
  )
  const hasMissingApproverInProcess = useMemo(() => {
    if (processState === undefined) return false
    return allKeys.some((key) =>
      hasMissingApprover(processState.cells.get(key), candidateIds),
    )
  }, [processState, allKeys, candidateIds])

  /**
   * 「保存」を押した時に出す、保存できない理由の文 (AC-APF-071)。 0 件の側は文から
   * 外す。 合算 1 文言だと「何を直せば保存できるか」が読めないため原因別に出す。
   * 英語は 1 文で語順が違うので、 組み合わせごとに 1 キーにしている。
   */
  const saveBlockerText =
    approverIncompleteCount > 0 && unsavedAutoBidCount > 0
      ? t('page:adminApprovalFlow.saveBlocker.both', {
          approver: approverIncompleteCount,
          autoBid: unsavedAutoBidCount,
        })
      : approverIncompleteCount > 0
        ? t('page:adminApprovalFlow.saveBlocker.approverOnly', {
            approver: approverIncompleteCount,
          })
        : unsavedAutoBidCount > 0
          ? t('page:adminApprovalFlow.saveBlocker.autoBidOnly', {
              autoBid: unsavedAutoBidCount,
            })
          : undefined

  const canSaveProcess =
    processState !== undefined &&
    canSave(processState, allKeys) &&
    unsavedAutoBidCount === 0

  // 止まっている状態で「保存」を押した回数。 0 = 押していない。 直し終える (理由が
  // 無くなる) と Alert は自然に消え、 変更を破棄した時にも 0 に戻す。
  const [saveAttempts, setSaveAttempts] = useState(0)
  // Alert の文の出どころ: 'save' = 「保存」 を押した / 'leave' = 「保存して移動」 で
  // 保存できなかった (AC-APF-011d。 文だけ業務処理名つきの別文言にする)。
  const [saveAttemptKind, setSaveAttemptKind] = useState<'save' | 'leave'>(
    'save',
  )
  // 「保存して移動」で保存できなかった時点の状態と自動入札ルール。 サーバーの拒否は
  // 画面側では未設定が見えないので、 編集 (processState) かルールの保存 (autoBidSlots)
  // で変わるまで Alert を立てておく。
  const [leaveRejected, setLeaveRejected] = useState<
    | {
        processState: ProcessState | undefined
        autoBidSlots: AutoBidSlotsByArea
      }
    | undefined
  >(undefined)
  const leaveSnapshotRef = useRef({ processState, autoBidSlots })
  leaveSnapshotRef.current = { processState, autoBidSlots }
  // フォーカスの要求回数。 「保存」 を押した時と、 「保存して移動」 で先頭の未設定になった時に増える。
  const [focusRequests, setFocusRequests] = useState(0)
  const saveAlertRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  /**
   * 最初の未設定セルへ送る (AC-APF-071)。 セルを直すボタンへスクロールして
   * フォーカスする。 マトリクスを畳んでいる時 (設定方法 = すべて要承認 / すべて自動) は
   * セルが描かれないので一括編集ボタンへ。 どちらも無い (切替条件の不正だけ等) 時は
   * Alert を見せる。
   */
  useEffect(() => {
    if (focusRequests === 0) return
    // 離脱確認ダイアログが開いている間は背面へフォーカスが入らず、 閉じた後に押す前の要素へ
    // フォーカスが戻されるので、 フォーカスが入るまで数フレーム試す。
    const MAX_FRAMES = 30
    let frame = 0
    let frames = 0
    const tryFocus = (): void => {
      frames += 1
      const marked =
        sectionRef.current?.querySelector<HTMLElement>(
          `[${SAVE_ERROR_ATTR}]`,
        ) ?? null
      // jsdom には scrollIntoView が無い
      if (marked === null) {
        saveAlertRef.current?.scrollIntoView?.({ block: 'nearest' })
        return
      }
      const target = fixControlOf(marked)
      target.focus({ preventScroll: true })
      if (document.activeElement === target) {
        target.scrollIntoView?.({ block: 'center', inline: 'nearest' })
        return
      }
      if (frames < MAX_FRAMES) frame = requestAnimationFrame(tryFocus)
    }
    frame = requestAnimationFrame(tryFocus)
    return () => cancelAnimationFrame(frame)
  }, [focusRequests])

  // 「保存して移動」で保存できなかった: 区画を開き、 同じ Alert を出す (AC-APF-011d)
  useEffect(() => {
    if (leaveBlockedCount === 0) return
    setIsOpen(true)
    setSaveAttemptKind('leave')
    setSaveAttempts((n) => n + 1)
    setLeaveRejected(leaveSnapshotRef.current)
  }, [leaveBlockedCount])
  useEffect(() => {
    if (leaveFocusCount === 0) return
    setFocusRequests((n) => n + 1)
  }, [leaveFocusCount])

  /**
   * 保存を止めている理由 (AC-APF-071)。 「保存」を押した時にだけ画面上部の Alert で出す。
   * 押す前に文を常時出さない・保存を非活性にしない (UI 辞書 components/action-reason.md の
   * 区分 1。 2026-10-14)。 押下時の拒否と Alert はこの 1 つの値から切り替える。
   *
   * [spec差異] AC-APF-071 は「保存を非活性にし、 理由を画面上に文で表示」。 8080
   * (`admin/approval-flow.html` の saveBlockers) と辞書は上記の形で、
   * conventions.md「操作できない時のボタンの扱い」 の書き換えを PdM に提案中。
   */
  const saveBlockerMessage =
    saveAttemptKind === 'leave'
      ? saveAttempts > 0 &&
        (incompleteCount > 0 ||
          (leaveRejected !== undefined &&
            leaveRejected.processState === processState &&
            leaveRejected.autoBidSlots === autoBidSlots))
        ? t('page:adminApprovalFlow.leaveGuard.saveIncomplete', {
            process: t(processDef.i18nKey),
          })
        : undefined
      : incompleteCount > 0
        ? saveBlockerText
        : undefined

  // 直し終えたら (保存を止める理由が無くなったら) 押した記録も下ろす。 下ろさないと、
  // 後で別のセルを未設定にした時に、 「保存」を押す前から Alert とセルのエラーが出る
  // (8080 `admin/approval-flow.html` が描画のたびに `_saveTried` を下ろすのと同じ)。
  // 描画中に自分の state を直す書き方なので、 effect を待たずに同じ描画で消える。
  if (saveAttempts > 0 && saveBlockerMessage === undefined) {
    setSaveAttempts(0)
  }
  const saveTried = saveAttempts > 0 && saveBlockerMessage !== undefined

  /**
   * 「保存」を押した後にエラー表示にするセル (AC-APF-071 / UI 辞書 action-reason.md
   * 区分 1)。 押す前は出さない。 Alert の件数と同じ数え方で、 直したセルは次の描画で
   * 外れる。
   */
  const saveErrorKeys = useMemo(
    (): ReadonlySet<CellKey> =>
      saveTried
        ? new Set([...approverIncompleteKeys, ...unsavedAutoBidKeys])
        : NO_CELL_KEYS,
    [saveTried, approverIncompleteKeys, unsavedAutoBidKeys],
  )

  const processLabel = t(processDef.i18nKey)

  // プロセス全体 一括セレクトの 4 値 (個別設定 / 全て要承認 / 全て自動 / 全て承認なし)。
  // DS `Select` の value は string なので proto enum を `String()` で載せる。
  // 並び順は design admin/approval-flow.html:1133 の BULK_OPTS
  // (個別設定 -> すべて要承認 -> すべて承認なし -> すべて自動)。 同じ画面の
  // 行一括 / 列一括 (matrixStyles.ts) は既にこの順で、 設定方法だけ
  // 「すべて自動」 と「すべて承認なし」 が逆になっていた (2026-08-24 デザイナー指摘)。
  const bulkAllOptions: DsSelectOptions = useMemo(
    () => [
      {
        value: String(ApprovalMode.UNSPECIFIED),
        label: t('page:adminApprovalFlow.matrix.bulkIndividual'),
      },
      {
        value: String(ApprovalMode.APPROVAL),
        label: t('page:adminApprovalFlow.matrix.bulkAllApproval'),
      },
      {
        value: String(ApprovalMode.NO_APPROVAL),
        label: t('page:adminApprovalFlow.matrix.bulkAllNoApproval'),
      },
      {
        value: String(ApprovalMode.AUTO),
        label: t('page:adminApprovalFlow.matrix.bulkAllAuto'),
      },
    ],
    [t],
  )

  // destructive リセット対象セル数 (= 承認なし既定から外れている or 段階が設定済みのセル。
  // 既定値は converter.ts の buildDefaultCells〈NO_APPROVAL, 段階未設定〉が SoT)。
  const resetTargetCount = useMemo(() => {
    if (processState === undefined) return 0
    return allKeys.filter((key) => {
      const cell = processState.cells.get(key)
      if (cell === undefined) return false
      if (cell.mode === ApprovalMode.NO_APPROVAL && cell.stages.length === 0) {
        return false
      }
      return true
    }).length
  }, [processState, allKeys])

  // セル集約から導出した一括 ApprovalMode (= 全セル同一なら そのモード、 混在なら UNSPECIFIED)
  const derivedBulk =
    processState !== undefined
      ? aggregateMode(processState.cells, allKeys)
      : ApprovalMode.UNSPECIFIED

  // 一括セレクトの表示値: 個別設定を明示選択中なら UNSPECIFIED、 それ以外は導出値
  const bulkMode = individualView ? ApprovalMode.UNSPECIFIED : derivedBulk

  // マトリクスを展開する条件 (AC-003):
  // - 個別設定 (UNSPECIFIED) のみマトリクスを展開し、各セルを個別に設定する (AC-004)。
  // - 個別設定以外 (要承認 / 自動 / 承認なし) は全セルを一括値で塗りつぶし、
  //   マトリクスを非表示にする。要承認時の承認者設定は「承認者一括編集」ボタンから行う。
  const showMatrix = bulkMode === ApprovalMode.UNSPECIFIED

  // 業務処理名が設定状況モーダルの入口になるのはマトリクスを表示している時だけ
  // (AC-APF-057。 一括の時の入口は AC-APF-074 の「設定状況を見る」)。 design は一括の
  // 時も業務処理名で開くが、 開閉の button の押せる面に残す (#12231 の Constraints)。
  const isMatrixShown = isOpen && hasState && showMatrix
  const processTitleId = useId()

  const stageRange = useMemo(
    () =>
      bulkMode === ApprovalMode.APPROVAL && processState !== undefined
        ? approvalStageRange(allKeys.map((key) => processState.cells.get(key)))
        : undefined,
    [bulkMode, processState, allKeys],
  )

  /**
   * マトリクスを畳んでいる間に保存を止めるセルがあれば、 一括編集ボタンを直す入口に
   * する (8080 は要約の `[data-appr-bulk]` / `[data-ab-bulk]` へ送る)。 ボタンの
   * 見た目は変えない。
   */
  const bulkSaveErrorProps =
    !showMatrix && saveErrorKeys.size > 0 ? { [SAVE_ERROR_ATTR]: '' } : {}

  /**
   * 自動入札ルールの state。ルール要約は **マトリクスの「自動」セル内** (AC-APF-037)、
   * 説明パネルはマトリクス直下 (AC-APF-035) と描画先が 2 つに分かれるので、
   * データと編集モーダルはここで 1 つに持って両方へ配る。
   *
   * 自動入札を持たない業務処理でも呼ぶ (hooks は条件分岐できない)。 bid-limits の
   * 取得は React Query の同一キーで、入札 2 業務処理と共有される 1 リクエストに
   * まとまるため、ここで呼んでも増えない。
   */
  const autoBid = useAutoBidSection({
    processDef,
    processState,
    autoBidSlots,
  })
  const hasAutoAreas = autoBid.autoAreas.length > 0

  // サマリ: 要承認 N / 自動 N / 承認なし N
  const summary = (() => {
    if (processState === undefined) return ''
    let approval = 0,
      auto = 0,
      noApproval = 0
    for (const cell of processState.cells.values()) {
      if (cell.mode === ApprovalMode.APPROVAL) approval++
      else if (cell.mode === ApprovalMode.AUTO) auto++
      else if (cell.mode === ApprovalMode.NO_APPROVAL) noApproval++
    }
    return t('page:adminApprovalFlow.processSummary', {
      approval,
      auto,
      noApproval,
    })
  })()

  /**
   * 一括セレクト変更ハンドラ。
   * 個別設定 (UNSPECIFIED) 選択 → no-op (= セル値を維持してマトリクス展開)。
   * 非個別設定 → 全セルを一括で当該モードに上書きし、マトリクスを非表示にする (AC-003)。
   * 要承認時の承認者設定は「承認者一括編集」ボタンから行う。
   */
  const handleBulkAll = useCallback(
    (mode: ApprovalMode) => {
      if (processState === undefined) return
      if (mode === ApprovalMode.UNSPECIFIED) {
        // 個別設定 = マトリクスを展開 (AC-004)。 セル値は変えない。
        setIndividualView(true)
        return
      }
      // DS `Select` は `emitOnReselect` を付けており、 具体モード (要承認 / 自動 /
      // 承認なし) を現在値と同じまま選び直しても onChange が届く (上の UNSPECIFIED
      // 分岐＝「導出の個別設定」→「明示的な個別設定」 の確定操作を拾うために必要)。
      // その同値再選択で dirty を立てない判定は reducer の `updateProcess` が実 diff で
      // 吸収するため、 ここでは無条件に BULK_SET_ALL を dispatch する。

      // 個別設定以外 = 全セルを当該モードで塗りつぶし、マトリクスを非表示にする (AC-003)。
      setIndividualView(false)
      dispatch({
        type: 'BULK_SET_ALL',
        businessProcess: processDef.businessProcess,
        areas: processDef.areas,
        mode,
      })
    },
    [dispatch, processDef, processState],
  )

  // 一括承認者モーダルの初期段階 (AC-APF-075): 全セルが 1 段以上の同じ承認段階
  // (段階の数と、 段階ごとの承認戦略・承認者がすべて同じ) を持つ時だけそれを引き継ぎ、
  // それ以外 (セルごとに異なる / 承認段階が無いセルがある / 新規) は空の 1 段階で開く。
  // 確定すると全セルに入るので、 1 エリアの承認者を気づかず他へ広げない。
  const bulkInitialStages: StageLocal[] =
    processState === undefined
      ? [makeEmptyStage()]
      : commonInitialStages(
          allKeys.map((key) => processState.cells.get(key)?.stages ?? []),
        )

  /**
   * 「承認者一括編集」モーダルの完了ハンドラ。
   * 設定した承認段階を全セル (全エリア) に一括適用する。
   * これにより 一括「全て要承認」で全セルが承認者未設定 = 保存ブロックされる状態を、
   * 1 回の操作で解消できる (各セルを個別に開かずに済む)。
   */
  const handleBulkStagesDone = useCallback(
    (stages: StageLocal[]) => {
      if (processState === undefined) return
      for (const key of allKeys) {
        dispatch({
          type: 'SET_CELL_STAGES',
          businessProcess: processDef.businessProcess,
          key,
          // セルごとに独立した stages インスタンスを渡す (参照共有を避ける)
          stages: stages.map((s) => ({
            ...s,
            approverMemberIds: [...s.approverMemberIds],
          })),
        })
      }
      setBulkStageModalOpen(false)
    },
    [allKeys, dispatch, processDef.businessProcess, processState],
  )

  const handleSave = useCallback(async () => {
    if (processState === undefined) return
    if (!canSaveProcess) {
      // 保存せず、 理由の Alert を出す (AC-APF-071)
      setSaveAttemptKind('save')
      setSaveAttempts((n) => n + 1)
      setFocusRequests((n) => n + 1)
      return
    }
    try {
      await saveProcess(processState, saveAsync, dispatch)
      setSaveAttempts(0)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'success',
        title: t('page:adminApprovalFlow.save.success', {
          process: processLabel,
        }),
      })
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'error',
        title: t('page:adminApprovalFlow.save.error'),
      })
    }
  }, [processState, canSaveProcess, saveAsync, dispatch, t, processLabel])

  // 変更を破棄: 当該業務処理の draft をサーバースナップショットに戻す (AC-APF-011c)。
  const handleDiscard = useCallback(() => {
    if (processState === undefined) return
    setIndividualView(false)
    setSaveAttempts(0)
    dispatch({
      type: 'RESET_PROCESS',
      businessProcess: processDef.businessProcess,
    })
    setDiscardConfirmOpen(false)
  }, [processState, processDef.businessProcess, dispatch])

  const handleReset = useCallback(async () => {
    if (processState === undefined) return
    try {
      // AC-APF-012a: リセットは空 payload を送って signal する。backend が
      // 既定 cells (全セル NO_APPROVAL, 段階未設定) を materialize し、
      // auto_bid_rule も clear する。明示 NO_APPROVAL 全セル payload は
      // reset として扱わない (ユーザーが AUTO セルを NO_APPROVAL に切替え
      // て通常保存した payload と shape が同一になり、auto_bid_rule が
      // 誤って silent 削除される regression を防ぐ)。
      const res = await resetAsync(
        create(SaveApprovalFlowRequestSchema, {
          businessProcess: processDef.businessProcess,
          cells: [],
          conditions: [],
          expectedUpdatedAt: processState.updatedAt,
        }),
      )
      // リセット完了をローカル state に即反映。空 cells で convertProcessSetting
      // を呼び buildDefaultCells 経路で組み直す (backend の defaultResetCellInputs
      // と同一 shape になる。セル展開の frontend↔backend 一致は既存の
      // processMap.ts / defaultResetCellInputs で担保)。getApprovalFlowScreen の
      // invalidate/refetch は他プロセスの dirty state を巻き戻さないよう
      // index.tsx の useEffect で dirty プロセスをスキップする。
      dispatch({
        type: 'INIT_PROCESS',
        processState: convertProcessSetting(
          create(ApprovalFlowProcessSettingSchema, {
            businessProcess: processDef.businessProcess,
            cells: [],
            updatedAt: res.updatedAt,
            autoBidRules: [],
          }),
        ),
      })
      setIndividualView(false)
      setSaveAttempts(0)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'success',
        title: t('page:adminApprovalFlow.resetConfirmModal.resetToast'),
      })
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'error',
        title: t('page:adminApprovalFlow.save.error'),
      })
    } finally {
      setResetConfirmOpen(false)
    }
  }, [processState, processDef.businessProcess, resetAsync, dispatch, t])

  return (
    <chakra.div
      ref={sectionRef}
      overflow="hidden"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
    >
      {/* アコーディオンヘッダー: processState が undefined (= loading) でも常に表示 */}
      <chakra.div
        textStyle="accordionTitle"
        position="relative"
        alignItems="center"
        display="flex"
        width="100%"
        padding="16px"
        background={eclColor.surfacePrimary}
        borderBottom={isOpen ? `1px solid ${eclColor.borderLow}` : 'none'}
        _hover={{ background: eclColor.hoverLow }}
        // design `css/components.css:4065` の
        // `.ecl-accordion-trigger:hover { background: var(--hover-low-emphasis) }`。
        // 押せる面なのに反応が無く、 クリックできると気づきにくかった。
        cursor="pointer"
      >
        {/* ガイドと業務処理名の button をヘッダーの中に置くため、ヘッダー全体を button
            にせず (button の入れ子は不正な HTML)、開閉の button の押せる面を `::after` で
            ヘッダー全体へ広げる。どちらの button も position を持ち (ガイドは DS が持つ)、
            DOM で後ろにあるので、その面より上に描かれて押せる。 */}
        <chakra.button
          alignItems="center"
          display="inline-flex"
          padding={0}
          color="inherit"
          textAlign="left"
          background="none"
          border="none"
          _after={{ content: '""', position: 'absolute', inset: 0 }}
          cursor="pointer"
          aria-expanded={isOpen}
          aria-labelledby={processTitleId}
          font="inherit"
          onClick={() => setIsOpen((v) => !v)}
          type="button"
        >
          {/* 開閉標は header の**左端**で、 DS `AccordionIconButton` と同じ見た目に
              揃える (2026-08-20 デザイナー指摘)。 閉じ = chevron-right / 開き =
              chevron-down の**グリフ差し替え**で、 回転ではない (design
              `css/components.css` の `.ecl-accordion-trigger::before` と同じ。
              回転方式だと閉じている時に上向き (▲) に見えてしまう)。 */}
          <DisclosureIcon expanded={isOpen} />

          {/* 業務処理名 (開閉の button の accessible name。 下の入口に出している間も
              aria-labelledby で指し続ける) */}
          {!isMatrixShown && (
            <chakra.span color="Text.highEmphasis" id={processTitleId}>
              {processLabel}
            </chakra.span>
          )}
        </chakra.button>

        {/* マトリクス表示中は業務処理名を設定状況モーダルの入口にする (AC-APF-057)。
            開閉の button の外・後ろに置いて `::after` の押せる面より上に出す。 */}
        {isMatrixShown && (
          <HintTooltip
            content={t('page:adminApprovalFlow.matrix.processTitleHint')}
          >
            <chakra.button
              position="relative"
              padding={0}
              color="Text.highEmphasis"
              background="none"
              border="none"
              cursor="pointer"
              font="inherit"
              id={processTitleId}
              onClick={() => setSectionViewOpen(true)}
              title={t('page:adminApprovalFlow.matrix.processTitleHint')}
              type="button"
            >
              {processLabel}
            </chakra.button>
          </HintTooltip>
        )}

        {isAutoBidProcess && (
          <chakra.span display="inline-flex" marginLeft="8px">
            <ApprovalModeGuide />
          </chakra.span>
        )}

        {/* 要設定バッジ。 design approval-flow.html L1024-1028 は
            `<span class="badge badge-error apr-proc-warn-badge">` = DS `Badge`
            status="negative" + 局所指定 (gap 4px / margin-left space-3) のみ。
            旧実装は raw span の独自 pill (badgeSmall 13px / Surface.negativeLow /
            radius full) で `~/constants/system.ts` の badge recipe が届いておらず、
            地色・寸法とも design から外れていた。 */}
        {incompleteCount > 0 && (
          <Badge
            alignItems="center"
            gap="4px"
            display="inline-flex"
            marginLeft="12px"
            whiteSpace="nowrap"
            aria-label={t('page:adminApprovalFlow.incompleteCount', {
              count: incompleteCount,
            })}
            // design `.apr-proc-warn-badge svg { flex-shrink: 0 }`
            css={{ '& svg': { flexShrink: 0 } }}
            role="status"
            status="negative"
          >
            <WarningTriangleIcon boxSize={iconSize.badgeIcon} />
            {t('page:adminApprovalFlow.incompleteCount', {
              count: incompleteCount,
            })}
          </Badge>
        )}

        {/* 欠落インジケータ (AC-APF-026d / Design Note D3)。 一括「全て要承認」時
            (showMatrix=false) は per-cell surface が無いため、 業務処理単位の
            非ブロック boolean 表示で欠落の有無を可視化する。 要設定バッジと
            独立の警告であり、 保存・完了はブロックしない (D2)。 */}
        {hasMissingApproverInProcess && (
          <Badge
            alignItems="center"
            gap="4px"
            display="inline-flex"
            marginLeft={incompleteCount > 0 ? '8px' : '12px'}
            whiteSpace="nowrap"
            aria-label={t('page:adminApprovalFlow.missingApproverIndicator')}
            css={{ '& svg': { flexShrink: 0 } }}
            role="status"
            status="negative"
          >
            <WarningTriangleIcon boxSize={iconSize.badgeIcon} />
            {t('page:adminApprovalFlow.missingApproverIndicator')}
          </Badge>
        )}

        {/* サマリ (右寄せ) */}
        <chakra.span
          textStyle="body"
          marginRight="12px"
          marginLeft="auto"
          color="Text.midEmphasis"
          fontWeight="normal"
        >
          {summary}
        </chakra.span>
      </chakra.div>

      {/* アコーディオン本体 */}
      {isOpen &&
        (processState === undefined ? (
          <RegionLoading minHeight="200px" isLoading />
        ) : (
          <>
            {/* 「保存」を押したが保存を止める理由がある時の Alert (AC-APF-071)。
                業務処理の区画の最上部に置く (業務処理ごとに保存するため。 UI 辞書
                alert-placement.md の 1)。 直すと自動で消える。 */}
            {saveAttempts > 0 && saveBlockerMessage !== undefined && (
              <chakra.div ref={saveAlertRef} margin="16px 16px 0">
                <CompactAlert
                  role="alert"
                  status="error"
                  title={saveBlockerMessage}
                />
              </chakra.div>
            )}

            {/* プロセス全体 一括セレクト (4値: 個別設定 / 全て要承認 / 全て自動 / 全て承認なし) */}
            <chakra.div
              alignItems="center"
              flexWrap="wrap"
              gap="8px"
              display="flex"
              padding="12px 16px"
            >
              <chakra.label
                textStyle="label"
                color="Text.highEmphasis"
                htmlFor={`bulk-all-${processDef.businessProcess}`}
              >
                {t('page:adminApprovalFlow.matrix.bulkAllLabel')}
              </chakra.label>
              {/* 単一 select の正は DS `Select` (`~/components/common/DsSelect`)。
                  `inputId` で上の `<label htmlFor>` と関連付ける
                  (DS Select は `id` を受けず react-select の `inputId` が本体)。 */}
              <DsSelect
                // DS `Select` (chakra-react-select) は枠・区切り・矢印で
                // chrome を約 91px 使う (border 2 + valueContainer paddingX 12×2 +
                // singleValue mx 2×2 + separator/gap + dropdownIndicator 12×2 + icon 20)。
                // 8080 の値は native select (chrome 38px) 前提なのでそのまま移植できず、
                // 表の中では文字領域が足りず選択値が欠けていた (2026-08-24 デザイナー指摘)。
                // 8080 の native select は最長 option に自動追従して実測 136px 固定。
                // DS Select は追従しないので「すべて承認なし」 (98px) + chrome 91px
                // = 190px を明示し、 値を切り替えても幅が動かないようにする。
                minWidth="190px"
                emitOnReselect
                inputId={`bulk-all-${processDef.businessProcess}`}
                onChange={(raw) => {
                  const mode = enumSetValueAs(raw)
                  if (mode !== undefined) handleBulkAll(mode as ApprovalMode)
                }}
                options={bulkAllOptions}
                value={bulkMode}
              />

              {/* 一括「全て要承認」時のみ: 全エリア一括の承認者設定モーダルを開く。
                  これが無いと全セルを個別に開かないと承認者を設定できず、
                  60 セルが「要承認+未設定」のまま保存不可になる。 */}
              {bulkMode === ApprovalMode.APPROVAL && (
                // 矢印の扱いは下の「全エリア一括編集」と同じ (デザイナー指摘
                // 2026-08-28)。 この 2 つは一括セレクトの値で排他表示される同じ
                // スロットなので、 片方だけボタン内に icon を残すと切り替えた瞬間に
                // 見た目が変わる。 design (8080) の `apr-bulk-approver-btn` も
                // icon を持たない。
                <chakra.div alignItems="center" gap="8px" display="flex">
                  <FaArrowRightRegular
                    boxSize={iconSize.gridInline}
                    color="Text.midEmphasis"
                    aria-hidden
                  />
                  <SecondaryButton
                    {...bulkSaveErrorProps}
                    onClick={() => setBulkStageModalOpen(true)}
                    size="md"
                  >
                    {t('page:adminApprovalFlow.matrix.bulkApproverButton')}
                  </SecondaryButton>
                  {/* AC-APF-074: 一括 = 要承認 でもセルごとに承認段階・承認者が異なる
                      ことがあるので、 設定状況モーダルを開く入口を並べる (design
                      `admin/approval-flow.html` の「設定状況を見る」)。 承認段階数
                      バッジは表示のみのまま。 */}
                  <SecondaryButton
                    onClick={() => setSectionViewOpen(true)}
                    size="md"
                  >
                    {t('page:adminApprovalFlow.matrix.viewSettingsButton')}
                  </SecondaryButton>
                </chakra.div>
              )}

              {/* AC-APF-037: 自動エリアが 1 件以上ある時に「全エリア一括編集」を出す。

                  **配置は一括セレクトの右** (デザイナー指摘 2026-09-14
                  「『→ 全エリア一括編集』を select の右に配置」)。 同指摘で
                  誘導文「下のボタンからルールを一括編集してください」 は削除した
                  (ボタンが隣にあるので要らない)。 同時に消えた「全 N エリアに
                  一括設定しています。」 は AC-APF-003a の要約として下の行に出す。

                  **個別設定 (UNSPECIFIED) でも出す** (同指摘「個別設定を選択した
                  場合も、 同じく select の右に導線配置」)。 個別設定はマトリクスの
                  セルごとに「ルール編集」 があるが、 自動エリアが複数ある時に
                  1 件ずつ開くのは現実的でないため、 一括導線を同じ位置に置く。

                  矢印はボタンの外・左 (デザイナー指摘 2026-08-28)。 ボタン内に
                  入れると label の一部に見えて誘導の意味が消える。 */}
              {isAutoBidProcess &&
                hasAutoAreas &&
                (bulkMode === ApprovalMode.AUTO ||
                  bulkMode === ApprovalMode.UNSPECIFIED) && (
                  <chakra.div alignItems="center" gap="8px" display="flex">
                    <FaArrowRightRegular
                      boxSize={iconSize.gridInline}
                      color="Text.midEmphasis"
                      aria-hidden
                    />
                    {/* 矢印の右に「現在設定中のルール」 の箱、 その右に一括編集
                        ボタン (デザイナー指摘 2026-09-14)。 以前は要約を下の独立行に
                        出していたが、 矢印 → ルール → 編集 の並びで 1 行に畳む。 */}
                    {autoBid.bulkSummary}
                    <SecondaryButton
                      {...bulkSaveErrorProps}
                      onClick={autoBid.openBulkEdit}
                      size="md"
                    >
                      {t(
                        autoBid.hasSavedRule
                          ? 'page:adminApprovalFlow.autoBid.bulkEditSaved'
                          : 'page:adminApprovalFlow.autoBid.bulkEditButton',
                      )}
                    </SecondaryButton>
                  </chakra.div>
                )}
            </chakra.div>

            {!showMatrix && (
              <chakra.div
                alignItems="center"
                gap="8px"
                display="flex"
                padding="4px 16px 16px"
                color="Text.highEmphasis"
                fontSize="md"
              >
                <FaArrowRightRegular
                  boxSize={iconSize.gridInline}
                  color="Text.midEmphasis"
                  aria-hidden
                />
                <chakra.span>
                  {t('page:adminApprovalFlow.matrix.bulkScopeSummary', {
                    areaCount: processDef.areas.length,
                  })}
                </chakra.span>
                {/* AC-APF-003b: 表示のみ。 承認段階は「承認者一括編集」 と設定状況モーダルから触る */}
                {stageRange !== undefined && (
                  <Badge whiteSpace="nowrap" status="neutral">
                    {stageRange.min === stageRange.max
                      ? t('page:adminApprovalFlow.cell.stageCount', {
                          count: stageRange.min,
                        })
                      : t('page:adminApprovalFlow.matrix.bulkStageRange', {
                          min: stageRange.min,
                          max: stageRange.max,
                        })}
                  </Badge>
                )}
              </chakra.div>
            )}

            {/* マトリクス: 個別設定(UNSPECIFIED)の時のみ表示 */}
            {showMatrix && (
              <ApprovalMatrix
                candidates={candidates}
                dispatch={dispatch}
                processDef={processDef}
                processLabel={processLabel}
                processState={processState}
                renderAutoBidCell={
                  isAutoBidProcess ? autoBid.renderCellSummary : undefined
                }
                saveErrorKeys={saveErrorKeys}
              />
            )}

            {/* AC-APF-035: 「常時表示」。 一括 = すべて要承認 / すべて承認なし では
                自動エリアを作れないので読む必要が無い、 という指摘を受けて一度
                その 2 モードで隠したが、 2026-08-28 デザイナー判断で **spec 準拠**
                に戻した (AC-APF-036 の空メッセージが 0 件時の受け皿になる)。 */}
            {isAutoBidProcess && (
              <AutoBidPanel
                hasAutoAreas={hasAutoAreas}
                isIntraday={
                  processDef.businessProcess === BusinessProcess.INTRADAY_BID
                }
              />
            )}

            {/* セル / 一括のどちらから開いても同じ 1 つを使うので、
                マトリクスの外で 1 回だけ描く。 */}
            {isAutoBidProcess && autoBid.editModal}

            {/* per-プロセス アクションフッター */}
            <chakra.div
              alignItems="center"
              justifyContent="flex-end"
              gap="8px"
              display="flex"
              padding="12px 16px"
              // 下端バーの地は全画面 **Surface.primary** で統一する (デザイナー指摘
              // 2026-08-27)。 他 6 本のバーは本 PR で揃えたが、 ここだけ
              // Surface.secondary で残っていた。
              background="Surface.primary"
              borderTopWidth="1px"
              borderTopColor="Border.lowEmphasis"
            >
              {/* 変更を破棄。当該業務処理の draft のみを破棄し saved 状態に戻す (AC-APF-011c)。
                  未保存変更があるときのみ活性。押下時は確認ダイアログを挟む。 */}
              {/* per-プロセス アクションフッターの 3 ボタンは design 側が
                  `.btn`(40px) = md (`.btn-sm` はマトリクス内の副次操作のみ)。 */}
              <TertiaryButton
                disabled={!processState.dirty || isPending}
                onClick={() => setDiscardConfirmOpen(true)}
                size="md"
              >
                {t('page:adminApprovalFlow.actions.discard')}
              </TertiaryButton>
              {/* destructive リセット。未保存変更の有無に関わらず実行可能 (AC-012a)。
                  押下時は確認ダイアログを挟む。 */}
              {SHOW_RESET_BUTTON && (
                <SecondaryButton
                  disabled={isPending}
                  loading={isResetting}
                  onClick={() => setResetConfirmOpen(true)}
                  size="md"
                >
                  {t('page:adminApprovalFlow.actions.reset')}
                </SecondaryButton>
              )}
              {/* 保存ボタンは未保存の変更がある時だけ活性 (AC-011)。 未設定セルなど保存を
                止める理由があっても非活性にせず、 押した時に上部の Alert で伝える
                (AC-APF-071)。 変更が無い時の非活性は理由が画面から分かるので
                tooltip も付けない (action-reason.md)。 */}
              <PrimaryButton
                disabled={!processState.dirty || isPending}
                loading={isPending}
                onClick={() => void handleSave()}
                size="md"
              >
                {t('page:adminApprovalFlow.actions.save')}
              </PrimaryButton>
            </chakra.div>
          </>
        ))}

      {/* 変更を破棄確認ダイアログ (AC-APF-011c)。
          OK で当該業務処理の draft を saved 状態に戻す。 */}
      <ConfirmDialog
        body={t('page:adminApprovalFlow.discardConfirm.body')}
        confirmLabel={t('page:adminApprovalFlow.discardConfirm.confirm')}
        destructive
        isOpen={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={() => void handleDiscard()}
        title={t('page:adminApprovalFlow.discardConfirm.title')}
      />

      {/* destructive リセット確認ダイアログ (AC-012a)。
          OK で saved 状態を既定値 (全エリア承認なし + 段階未設定) で上書き保存。 */}
      <ConfirmDialog
        body={
          <chakra.div whiteSpace="pre-line">
            {`${t('page:adminApprovalFlow.resetConfirmModal.summaryFormat', {
              process: processLabel,
              count: resetTargetCount,
            })}

${t('page:adminApprovalFlow.resetConfirmModal.inUseDesc', {
  process: processLabel,
})}

${t('page:adminApprovalFlow.resetConfirmModal.unexecutableDesc', {
  process: processLabel,
})}`}
          </chakra.div>
        }
        confirmLabel={t('page:adminApprovalFlow.resetConfirmModal.confirmNow')}
        destructive
        isLoading={isResetting}
        isOpen={resetConfirmOpen}
        onCancel={() => setResetConfirmOpen(false)}
        onConfirm={() => void handleReset()}
        title={t('page:adminApprovalFlow.resetConfirmModal.title')}
      />

      {/* 設定状況モーダル (AC-APF-057 / AC-APF-074)。 「編集はセルクリックから」の
          ヒントは、 押せるセルがあるマトリクス表示中だけ出す (AC-APF-057)。 */}
      {processState !== undefined && sectionViewOpen && (
        <SectionViewModal
          areas={processDef.areas}
          businessProcess={processDef.businessProcess}
          candidates={candidates}
          cells={processState.cells}
          hideHint={!isMatrixShown}
          isOpen
          onClose={() => setSectionViewOpen(false)}
          processLabel={processLabel}
        />
      )}

      {/* 全エリア一括の承認者設定モーダル (一括「全て要承認」用)。
          onCopyToOtherAreas を渡さない = コピーボタン非表示 (全エリア対象のため不要)。 */}
      {processState !== undefined && (
        <StageEditModal
          candidates={candidates}
          initialStages={bulkInitialStages}
          isOpen={bulkStageModalOpen}
          onClose={() => setBulkStageModalOpen(false)}
          onDone={handleBulkStagesDone}
          subtitle={t('page:adminApprovalFlow.stageModal.bulkSubtitle')}
          title={t('page:adminApprovalFlow.stageModal.bulkTitleFormat', {
            process: processLabel,
          })}
        />
      )}
    </chakra.div>
  )
}
